import React, { useContext } from 'react';
import { Outlet } from 'react-router-dom';
import {
    Avatar,
    Drawer,
    Dropdown,
    Layout,
    Menu,
    Spin,
    Tooltip,
} from 'antd';
import {
    DownOutlined,
    MenuFoldOutlined,
    MenuOutlined,
    MenuUnfoldOutlined,
    MoonOutlined,
    SunOutlined,
    UserOutlined,
} from '@ant-design/icons';

import './style.css';

import { ThemeContext } from '../../contexts/ThemeContext';
import ImagemLogoHorizontal from '../../assets/images/logo-govfacil.png';
import useLayoutAdmin from './useLayoutAdmin';
import useAuth from '../../hooks/useAuth';
import SelectCityModal from '../SelectCityModal';
import SidebarHeader from './components/SidebarHeader';
import GlobalSearch from './components/GlobalSearch';

const { Header, Sider, Content, Footer } = Layout;

export default function LayoutAdmin() {
    const { theme, updateTheme } = useContext(ThemeContext);
    const { activeCity } = useAuth();
    const isDark = theme === 'dark';

    const {
        abrirMenuLateral,
        clickMenuPrincipal,
        collapsed,
        collapsedWidth,
        itemsMenuPainelState,
        onClickMenuConfiguracoes,
        onCloseDrawer,
        onCollapse,
        posicaoMenu,
        setAbrirMenuLateral,
        visibleDrawer,
        itemsMenuConfiguracaoState,
        nomeUsuarioLogado,
        toggle,
    } = useLayoutAdmin();

    return (
        <>
            <Drawer
                placement="left"
                closable={false}
                onClose={onCloseDrawer}
                open={visibleDrawer}
                key="left"
                styles={{
                    body: {
                        padding: '12px 0 16px 0',
                        background: isDark ? '#0d1117' : '#0f172a',
                    },
                }}
            >
                <SidebarHeader collapsed={false} />
                <Menu
                    theme="dark"
                    mode="inline"
                    onClick={(position) => {
                        clickMenuPrincipal(position);
                    }}
                    selectedKeys={[posicaoMenu]}
                    openKeys={abrirMenuLateral}
                    onOpenChange={(openKeys) => setAbrirMenuLateral(openKeys)}
                    className="DrawerMenuPrincipal modern-sider-menu"
                    items={itemsMenuPainelState}
                />
            </Drawer>

            <Layout style={{ minHeight: '100vh' }}>
                <Sider
                    width={280}
                    collapsedWidth={collapsedWidth}
                    breakpoint="md"
                    onBreakpoint={(broken) => {
                        onCollapse(broken);
                    }}
                    collapsed={collapsed}
                    className="modern-admin-sider"
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'fixed',
                        left: 0,
                        zIndex: 100,
                        background: isDark ? '#0d1117' : '#0f172a',
                        borderRight: `1px solid ${isDark ? '#21262d' : 'rgba(255, 255, 255, 0.08)'}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <SidebarHeader collapsed={collapsed} />

                    <Menu
                        theme="dark"
                        mode="inline"
                        className="modern-sider-menu"
                        style={{
                            borderInlineEnd: 'none',
                        }}
                        onClick={({ keyPath }) => {
                            clickMenuPrincipal({ keyPath });
                        }}
                        selectedKeys={[posicaoMenu]}
                        onOpenChange={(openKeys) => setAbrirMenuLateral(openKeys)}
                        openKeys={abrirMenuLateral}
                        items={itemsMenuPainelState}
                    />
                </Sider>

                <Layout className={collapsed ? 'siteLayout2' : 'siteLayout'}>
                    <Header
                        style={{
                            position: 'fixed',
                            zIndex: 99,
                            padding: '0 20px',
                            width:
                                collapsedWidth === 0
                                    ? '100%'
                                    : collapsed
                                        ? 'calc(100% - 77px)'
                                        : 'calc(100% - 280px)',
                            backgroundColor: isDark
                                ? 'rgba(20, 20, 20, 0.95)'
                                : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            boxShadow: isDark
                                ? '0 2px 10px rgba(0, 0, 0, 0.3)'
                                : '0 1px 4px rgba(0, 0, 0, 0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            height: 64,
                            transition:
                                'width 0.2s cubic-bezier(0.2, 0, 0, 1), margin-left 0.2s cubic-bezier(0.2, 0, 0, 1), background-color 0.2s ease',
                        }}
                    >
                        {/* Left Trigger Button */}
                        <div
                            className="header-trigger-btn"
                            onClick={toggle}
                            style={{
                                background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
                                color: isDark ? '#e2e8f0' : '#334155',
                                border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                            }}
                        >
                            {collapsedWidth === 0 ? (
                                <MenuOutlined />
                            ) : collapsed ? (
                                <MenuUnfoldOutlined />
                            ) : (
                                <MenuFoldOutlined />
                            )}
                        </div>

                        {/* Center: Global Search Input */}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', maxWidth: 440, minWidth: 0, width: '100%' }}>
                            <GlobalSearch />
                        </div>

                        {/* Right: Theme Toggle + User Profile */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {/* Direct Theme Toggle Button */}
                            <Tooltip title={isDark ? "Mudar para modo claro" : "Mudar para modo escuro"}>
                                <button
                                    type="button"
                                    className={`navbar-theme-toggle-btn ${isDark ? 'dark' : ''}`}
                                    onClick={() => updateTheme(isDark ? 'light' : 'dark')}
                                >
                                    {isDark ? (
                                        <SunOutlined style={{ color: '#fbbf24', fontSize: 16 }} />
                                    ) : (
                                        <MoonOutlined style={{ color: '#6366f1', fontSize: 16 }} />
                                    )}
                                </button>
                            </Tooltip>

                            {/* User Profile Chip */}
                            <Dropdown
                                className="dropDownMenu"
                                trigger={['click']}
                                placement="bottomRight"
                                menu={{
                                    items: itemsMenuConfiguracaoState,
                                    onClick: onClickMenuConfiguracoes,
                                }}
                            >
                                <div className={`navbar-user-chip ${isDark ? 'navbar-user-chip-dark' : ''}`}>
                                    <Avatar
                                        size={28}
                                        icon={<UserOutlined />}
                                        style={{
                                            background: 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)',
                                            color: '#fff',
                                            fontSize: 13,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {collapsedWidth !== 0 && (
                                        <div className="navbar-user-info">
                                            <span className="navbar-user-name">
                                                {nomeUsuarioLogado}
                                            </span>
                                        </div>
                                    )}
                                    <DownOutlined className="navbar-user-chevron" />
                                </div>
                            </Dropdown>
                        </div>
                    </Header>
                    <Content
                        style={{
                            margin: '12px',
                            marginTop: '74px',
                            padding: '20px 24px',
                            minHeight: '160px',
                        }}
                    >
                        {activeCity ? (
                            <Outlet />
                        ) : (
                            <div
                                style={{
                                    minHeight: 'calc(100vh - 220px)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 16,
                                    textAlign: 'center',
                                    padding: '40px 20px',
                                }}
                            >
                                <Spin size="large" />
                                <div style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 14, fontWeight: 500 }}>
                                    Aguardando seleção do município...
                                </div>
                            </div>
                        )}
                    </Content>
                    <Footer
                        style={{
                            textAlign: 'center',
                            marginTop: -10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingLeft: 0,
                            paddingRight: 0,
                            fontSize: 12.5,
                            color: isDark ? '#8c8c8c' : '#64748b',
                        }}
                    >
                        Desenvolvido por{' '}
                        <img
                            style={{
                                width: 85,
                                marginLeft: 6,
                                opacity: 0.9,
                            }}
                            src={ImagemLogoHorizontal}
                            alt="GovFácil"
                        />
                    </Footer>
                </Layout>
            </Layout>

            <SelectCityModal />
        </>
    );
}
